<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('backgroundCheck.list.title') }}</h4>
					<p class="mb-0">{{ $t('backgroundCheck.list.description') }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-4">
					<button class="btn btn-outline-primary" @click="copyPublicLink" v-if="publicToken">
						<i class="ri-link me-1"></i> {{ $t('backgroundCheck.list.copyLink') }}
					</button>
					<button class="btn btn-primary" @click="enableFeature" v-if="!publicToken && !featureLoading">
						<i class="ri-shield-check-line me-1"></i> {{ $t('backgroundCheck.list.enableFeature') }}
					</button>
				</div>
			</div>

			<!-- Public link info -->
			<div class="alert alert-info mb-4" v-if="linkCopied">
				<i class="ri-checkbox-circle-line me-1"></i> {{ $t('backgroundCheck.list.linkCopied') }}
			</div>

			<div class="col-lg-12" v-if="publicToken">
				<div class="card">
					<div class="card h-100">
						<div class="card-body pb-0">
							<form class="dt_adv_search">
								<div class="row">
									<div class="col-12">
										<div class="row g-5">
											<div class="col-12 col-sm-6 col-lg-6">
												<div class="form-floating form-floating-outline">
													<input
														type="text"
														class="form-control"
														:placeholder="$t('backgroundCheck.list.searchPlaceholder')"
														v-model="search"
														@input="applyFilters"
														autocomplete="off"
													/>
													<label>{{ $t('backgroundCheck.list.searchPlaceholder') }}</label>
												</div>
											</div>
											<div class="col-12 col-sm-6 col-lg-6">
												<div class="form-floating form-floating-outline">
													<select class="form-select" v-model="statusFilter" @change="applyFilters">
														<option value="">{{ $t('backgroundCheck.list.allStatuses') }}</option>
														<option value="submitted">{{ $t('backgroundCheck.status.submitted') }}</option>
														<option value="in-review">{{ $t('backgroundCheck.status.inReview') }}</option>
														<option value="approved">{{ $t('backgroundCheck.status.approved') }}</option>
														<option value="rejected">{{ $t('backgroundCheck.status.rejected') }}</option>
													</select>
													<label>{{ $t('common.fields.status') }}</label>
												</div>
											</div>
										</div>
									</div>
								</div>
							</form>
						</div>
						<div class="table-responsive">
							<table class="table table-borderless">
								<thead>
									<tr class="border-bottom">
										<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.list.candidate') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.list.submissionDate') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.list.gapsDetected') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('backgroundCheck.list.identity') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('common.fields.status') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('common.fields.actions') }}</th>
									</tr>
								</thead>
								<tbody class="table-border-bottom-0">
									<tr v-if="filteredChecks.length === 0">
										<td colspan="6" class="text-center text-muted py-4">{{ $t('backgroundCheck.list.noResults') }}</td>
									</tr>
									<tr v-for="(check, index) in filteredChecks" :key="index" style="cursor: pointer;" @click="goToDetail(check._id)">
										<td>
											<span class="fw-medium">{{ check.firstName }} {{ check.lastName }}</span>
											<small class="text-muted ms-1" v-if="check.birthDate">({{ getAge(check.birthDate) }} {{ $t('common.years') }})</small>
										</td>
										<td>
											<span class="fw-medium" :title="formatDate(check.submittedAt)">{{ fromNow(check.submittedAt) }}</span>
										</td>
										<td>
											<span class="badge bg-label-warning rounded-pill" v-if="check.gapAnalysis && check.gapAnalysis.gaps && check.gapAnalysis.gaps.length > 0">
												{{ check.gapAnalysis.gaps.length }} gap{{ check.gapAnalysis.gaps.length > 1 ? 's' : '' }}
											</span>
											<span class="badge bg-label-secondary rounded-pill" v-else-if="check.gapAnalysis && check.gapAnalysis.status === 'completed'">
												{{ $t('common.none') }}
											</span>
											<span class="text-muted" v-else>
												—
											</span>
										</td>
										<td>
											<span class="badge rounded-pill" :class="veriffBadgeClass(check.verification)">
												{{ veriffLabel(check.verification) }}
											</span>
										</td>
										<td>
											<span class="badge rounded-pill" :class="statusBadgeClass(check.status)">
												{{ statusLabel(check.status) }}
											</span>
										</td>
										<td>
											<div class="d-flex gap-1">
												<button class="btn btn-sm btn-outline-primary" @click.stop="goToDetail(check._id)">
													<i class="ri-eye-line"></i>
												</button>
												<button v-if="check.status === 'approved'" class="btn btn-sm btn-outline-primary"
													@click.stop="exportPdf(check)" :disabled="exportingId === check._id"
													:title="$t('backgroundCheck.detail.exportPdf')">
													<span v-if="exportingId === check._id" class="spinner-border spinner-border-sm"></span>
													<i v-else class="ri-download-line"></i>
												</button>
												<button class="btn btn-sm btn-outline-secondary" @click.stop="archiveCheck(check._id)" :title="$t('common.actions.archive')">
													<i class="ri-archive-line"></i>
												</button>
											</div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>

			<!-- Feature not enabled -->
			<div class="card" v-if="!publicToken && !featureLoading">
				<div class="card-body text-center py-6">
					<i class="ri-shield-check-line" style="font-size: 48px; color: var(--bs-primary);"></i>
					<h5 class="mt-4">{{ $t('backgroundCheck.list.title') }}</h5>
					<p class="text-muted">{{ $t('backgroundCheck.list.featureDescription') }}</p>
					<button class="btn btn-primary" @click="enableFeature">{{ $t('common.actions.activate') }}</button>
				</div>
			</div>
		</div>
		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import moment from "moment";
import "moment/locale/fr";

moment.locale(localStorage.getItem('locale') || 'fr');

export default {
	name: "BackgroundCheckList",

	data() {
		return {
			checks: [],
			publicToken: null,
			featureLoading: true,
			linkCopied: false,
			search: "",
			statusFilter: "",
			exportingId: null,
		};
	},

	computed: {
		filteredChecks() {
			let result = this.checks;

			if (this.search) {
				const s = this.search.toLowerCase();
				result = result.filter(c =>
					(c.firstName + " " + c.lastName).toLowerCase().includes(s) ||
					(c.email || "").toLowerCase().includes(s)
				);
			}

			if (this.statusFilter) {
				result = result.filter(c => c.status === this.statusFilter);
			}

			return result;
		},
	},

	methods: {
		async fetchChecks() {
			try {
				const response = await HTTP("/background-check/");
				this.checks = response.data.checks;
				this.publicToken = response.data.publicToken;
			} catch (error) {
				if (error?.response?.status === 404 || error?.response?.status === 403) {
					this.publicToken = null;
				} else {
					const msg = error?.response?.data?.message || this.$t('errors.loadingError');
					alert(msg);
				}
			} finally {
				this.featureLoading = false;
			}
		},

		async exportPdf(check) {
			this.exportingId = check._id;
			try {
				const response = await HTTP.get(`/background-check/${check._id}/recap-pdf`);
				if (response.data?.base64) {
					const byteCharacters = atob(response.data.base64);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = `Dossier-${check.lastName || ''}-${check.firstName || ''}.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					URL.revokeObjectURL(link.href);
				} else {
					throw new Error("Fichier non disponible.");
				}
			} catch (error) {
				alert(error?.response?.data?.message || this.$t('backgroundCheck.detail.exportError'));
			} finally {
				this.exportingId = null;
			}
		},

		async enableFeature() {
			try {
				const response = await HTTP.post("/background-check/enable");
				this.publicToken = response.data.publicToken;
				alert(this.$t('backgroundCheck.list.featureActivated'));
				await this.fetchChecks();
			} catch (error) {
				const msg = error?.response?.data?.message || this.$t('backgroundCheck.list.activationError');
				alert(msg);
			}
		},

		copyPublicLink() {
			const url = `${window.location.origin}/verification/${this.publicToken}`;
			navigator.clipboard.writeText(url).then(() => {
				this.linkCopied = true;
				setTimeout(() => { this.linkCopied = false; }, 3000);
			});
		},

		async archiveCheck(id) {
			if (!confirm(this.$t('backgroundCheck.list.archiveConfirm'))) return;
			try {
				await HTTP.put(`/background-check/${id}/status`, { status: "archived" });
				this.checks = this.checks.filter(c => c._id !== id);
			} catch (error) {
				alert(error?.response?.data?.message || this.$t('backgroundCheck.list.archiveError'));
			}
		},

		goToDetail(id) {
			this.$router.push(`/background-check/detail/${id}`);
		},

		formatDate(date) {
			if (!date) return "—";
			return moment(date).format("DD/MM/YYYY HH:mm");
		},

		fromNow(date) {
			if (!date) return "—";
			return moment(date).fromNow();
		},

		getAge(birthDate) {
			if (!birthDate) return null;
			return moment().diff(moment(birthDate), "years");
		},

		statusBadgeClass(status) {
			const map = {
				"submitted": "bg-label-info",
				"in-review": "bg-label-warning",
				"approved": "bg-label-success",
				"rejected": "bg-label-danger",
			};
			return map[status] || "bg-label-secondary";
		},

		statusLabel(status) {
			const map = {
				"submitted": this.$t('backgroundCheck.status.submitted'),
				"in-review": this.$t('backgroundCheck.status.inReview'),
				"approved": this.$t('backgroundCheck.status.approved'),
				"rejected": this.$t('backgroundCheck.status.rejected'),
			};
			return map[status] || status;
		},

		veriffBadgeClass(verification) {
			if (!verification || !verification.status) return "bg-label-secondary";
			const map = {
				"init": "bg-label-secondary",
				"started": "bg-label-info",
				"submitted": "bg-label-warning",
				"approved": "bg-label-success",
				"declined": "bg-label-danger",
				"resubmission_requested": "bg-label-warning",
				"expired": "bg-label-danger",
				"abandoned": "bg-label-danger",
			};
			return map[verification.status] || "bg-label-secondary";
		},

		veriffLabel(verification) {
			if (!verification || !verification.status) return "—";
			const map = {
				"init": this.$t('backgroundCheck.verification.pending'),
				"started": this.$t('backgroundCheck.verification.inProgress'),
				"submitted": this.$t('backgroundCheck.verification.submitted'),
				"approved": this.$t('backgroundCheck.verification.verified'),
				"declined": this.$t('backgroundCheck.verification.refused'),
				"resubmission_requested": this.$t('backgroundCheck.verification.resubmission'),
				"expired": this.$t('backgroundCheck.verification.expired'),
				"abandoned": this.$t('backgroundCheck.verification.abandoned'),
			};
			return map[verification.status] || verification.status;
		},

		applyFilters() {
			// Reactive via computed — no action needed
		},
	},

	async mounted() {
		await this.fetchChecks();
	},
};
</script>
